import React, { createContext, useContext, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
// The fonts the builder's Marcellus / Jost tokens name (LandingBuilder/tokens.js) —
// bundled with the app, because a guest on this page is not online yet and could
// not fetch a web font.
import '@fontsource/marcellus/400.css';
import '@fontsource/jost/400.css';
import '@fontsource/jost/500.css';
import '@fontsource/jost/600.css';
import { AUTH_METHOD_REGISTRY } from './authRegistry';
import {
    buildResponsiveCss, buildLoginThemeCss, styleClassFor, themeClassFor, lookClassFor, lookOf, pageLookOf, CONTAINER_TYPES,
} from './landingPageStyles';
import { getBackgroundLayers, backgroundStyleFor } from './backgroundLayers';
import ScenicPortal from './scenic/ScenicPortal';
import TermsModal from './TermsModal';

/**
 * Renders a published visual-builder LandingPage's node tree for guests.
 * Additive/opt-in — only reached when portalData.landingPage is present
 * (see Landing.jsx's early branch and backend/controllers/portal.controller.js's
 * getLandingPage). Never touches the two existing hardwired rendering
 * paths (default 'custom' preset / 'oakwood-asr') in Landing.jsx.
 *
 * Auth smart-components (otp-login/pms-login/voucher-login/
 * auth-method-selector) delegate to the EXISTING, unmodified
 * authRegistry.js components — this renderer never reimplements auth
 * logic, only presentational layout around it. `authContext` bundles the
 * state/handlers Landing.jsx already owns (formData, handleSubmit, etc.)
 * so the guest login flow behaves identically regardless of which
 * renderer produced the surrounding markup.
 *
 * Node shape is validated server-side by backend/utils/landingPageSchema.js
 * before ever being persisted — this component trusts that shape (fixed
 * type allowlist, no arbitrary markup/HTML injection surface) but still
 * defaults defensively (missing props/style never throw).
 */

// The look of the page's login box (props.look on its login nodes), which the
// connected / can't-connect screens take too.
const PageLookContext = createContext(null);

/** The first node of a type on the page, at any depth. */
function findNode(nodes, type) {
    for (const node of nodes || []) {
        if (node.type === type) return node;
        const inside = findNode(node.children, type);
        if (inside) return inside;
    }
    return null;
}

/** Whether a page has a box marked to show the connected / can't-connect screens. */
export function hasStatusPanel(nodes) {
    return (nodes || []).some((node) => (
        (CONTAINER_TYPES.includes(node.type) && node.props?.statusPanel === 'yes')
        // The Scenic sign-in page shows them in its own card.
        || node.type === 'scenic-portal'
        || hasStatusPanel(node.children)
    ));
}

// Company branding (Branches / Tenant Profile → Branding, see
// backend/utils/portalBrand.js) for the brand tokens: var(--lp-brand, <default>).
function brandVariables(portalData) {
    const variables = {};
    if (portalData.brandColor) variables['--lp-brand'] = portalData.brandColor;
    if (portalData.brandAccentColor) variables['--lp-accent'] = portalData.brandAccentColor;
    return variables;
}

export default function LandingPageRenderer({ nodes, portalData, authContext }) {
    const { showTermsModal, setShowTermsModal, handleSubmit } = authContext;
    // Real @media-query CSS (not JS breakpoint switching) — see
    // landingPageStyles.js's doc comment for why: guest devices are real
    // browsers at unknown, uncontrolled viewport widths. (The builder's live
    // preview renders this same component inside an iframe, so the real media
    // queries respond to the preview's width exactly as on a device.)
    const css = [buildResponsiveCss(nodes), buildLoginThemeCss(nodes)].filter(Boolean).join('\n');

    return (
        <PageLookContext.Provider value={pageLookOf(nodes)}>
            <div className="lp-render-root" style={brandVariables(portalData)}>
                {css && <style>{css}</style>}
                <form className="portal-form lp-render-form" onSubmit={handleSubmit}>
                    {(nodes || []).map((node) => (
                        <LPNode key={node.id} node={node} portalData={portalData} authContext={authContext} />
                    ))}

                    {/* Nothing is added automatically after the page's own content: the
                        Terms checkbox + Connect button only appear where the page puts a
                        `login-submit` block. (The builder warns, and refuses to publish, a
                        page that has a login form but no such block.) */}
                </form>

                {/* The platform's credit line, on every built page (the Scenic page carries its own). */}
                {!(nodes || []).some((node) => node.type === 'scenic-portal') && (
                    <p className="lp-credit" style={{ margin: '12px 16px 16px', textAlign: 'center', fontSize: '12px', lineHeight: 1.4, opacity: 0.7 }}>
                        Powered by HotWiFi · Developed by AsyncPulse Technologies
                    </p>
                )}

                {/* The same popup as the classic portal pages (the .modal-overlay /
                    .modal-content used here before had no styles: the terms opened as
                    plain text below the page). Its wording: the page's Terms + Connect
                    block's, else the branch's Terms & Conditions, else the standard terms. */}
                {showTermsModal && (() => {
                    const submit = findNode(nodes, 'login-submit')?.props || {};
                    return (
                        <TermsModal
                            title={submit.termsTitle || undefined}
                            text={submit.termsText || portalData.termsAndConditions}
                            onClose={() => setShowTermsModal(false)}
                        />
                    );
                })()}
            </div>
        </PageLookContext.Provider>
    );
}

/** The terms checkbox + Connect button — the body of a page-placed `login-submit` block. */
function TermsAndSubmit({ authContext, text, agreeText, termsLinkText, buttonClassName }) {
    return (
        <>
            <div className="terms-container">
                <label className="checkbox-wrapper">
                    <input
                        type="checkbox"
                        name="agreedToTerms"
                        checked={!!authContext.formData.agreedToTerms}
                        onChange={authContext.handleInputChange}
                    />
                    <span className="checkbox-text">
                        {agreeText || 'I agree to the'}{' '}
                        <a href="#" className="lp-terms-link" onClick={(e) => { e.preventDefault(); authContext.setShowTermsModal(true); }}>
                            {termsLinkText || <>Terms &amp; Conditions</>}
                        </a>
                    </span>
                </label>
            </div>

            <button type="submit" className={buttonClassName || 'btn btn-primary btn-lg w-full mt-4'} disabled={authContext.loading}>
                {authContext.loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                    text || 'Connect'
                )}
            </button>
        </>
    );
}

/** Combines a component's static base class with its per-node responsive
 * style class (from landingPageStyles.js) — the latter is only meaningful
 * if buildResponsiveCss() actually found style rules for this node, but
 * an unmatched class name is harmless either way. */
// Pictures and buttons keep their own size when a layout stacks on a small screen.
const KEEP_OWN_WIDTH = ['image', 'logo', 'icon', 'button', 'free-access-button', 'qr-code'];

function classFor(node, baseClass) {
    // The tags below are what the fixed "Automatic layout" @media block in Landing.css
    // selects — see there. They only describe the node, so they need no parent context.
    const auto = [
        node.style?.desktop?.position === 'absolute' ? 'lp-floating' : '',
        KEEP_OWN_WIDTH.includes(node.type) ? 'lp-natural' : '',
        CONTAINER_TYPES.includes(node.type) && node.props?.layout === 'free' ? 'lp-free' : '',
    ].filter(Boolean).join(' ');
    return [baseClass, styleClassFor(node), auto].filter(Boolean).join(' ');
}

/** The wrapper classes of a login node: its own, its colour theme and its look. */
function loginClassFor(node, baseClass) {
    return [baseClass, themeClassFor(node), lookClassFor(node)].filter(Boolean).join(' ');
}

function renderChildren(node, portalData, authContext) {
    return (node.children || []).map((child) => (
        <LPNode key={child.id} node={child} portalData={portalData} authContext={authContext} />
    ));
}

function LPNode({ node, portalData, authContext }) {
    const { type, props = {} } = node;

    switch (type) {
        // ── Layout ───────────────────────────────────────────────────
        case 'section':
        case 'container':
            // After sign-in, the box marked for it shows the outcome in place of its content.
            if (props.statusPanel === 'yes' && authContext.status) {
                return (
                    <div className={[classFor(node, `lp-${type}`), props.statusInset === 'yes' ? 'lp-status-inset' : ''].filter(Boolean).join(' ')}>
                        <StatusScreen status={authContext.status} portalData={portalData} onBack={authContext.clearStatus} />
                    </div>
                );
            }
            return <div className={classFor(node, `lp-${type}`)}>{renderChildren(node, portalData, authContext)}</div>;
        case 'row':
            return <div className={classFor(node, 'lp-row')}>{renderChildren(node, portalData, authContext)}</div>;
        case 'column':
            return <div className={classFor(node, 'lp-column')}>{renderChildren(node, portalData, authContext)}</div>;
        case 'grid':
            return <div className={classFor(node, 'lp-grid')}>{renderChildren(node, portalData, authContext)}</div>;
        case 'spacer':
            return <div className={classFor(node, 'lp-spacer')} style={{ height: props.height || '24px' }} />;
        case 'divider':
            return <hr className={classFor(node, 'lp-divider')} />;
        case 'background':
            return (
                <div className={classFor(node, 'lp-background')} style={backgroundStyleFor(getBackgroundLayers(node))}>
                    {renderChildren(node, portalData, authContext)}
                </div>
            );

        // ── Content ──────────────────────────────────────────────────
        case 'heading': {
            const Tag = ['h1', 'h2', 'h3', 'h4'].includes(props.level) ? props.level : 'h2';
            return <Tag className={classFor(node)}>{props.text || ''}</Tag>;
        }
        case 'paragraph':
            return <p className={classFor(node)}>{props.text || ''}</p>;
        case 'richtext':
            return <RichText text={props.text} href={props.href} className={classFor(node)} />;
        case 'image':
            return props.src ? <img src={props.src} alt={props.alt || ''} className={classFor(node, 'lp-image')} /> : null;
        case 'logo': {
            // The page's own picture, else the company logo from its branding.
            const src = props.src || portalData.logo;
            if (src) return <img src={src} alt="Logo" className={classFor(node, 'lp-logo')} />;
            return props.nameFallback === 'yes' ? <Wordmark node={node} portalData={portalData} /> : null;
        }
        case 'button':
            return <button type="button" className={classFor(node, 'btn btn-secondary')}>{props.text || 'Button'}</button>;
        case 'icon':
            return <i className={classFor(node, `fas ${props.icon || 'fa-star'}`)}></i>;
        case 'video':
            return props.src ? <video src={props.src} controls className={classFor(node)} /> : null;

        // ── HotWifi smart components — delegate to authRegistry.js,
        // never render arbitrary markup for these. ────────────────────
        case 'pms-login':
        case 'voucher-login':
        case 'otp-login':
        case 'resident-login':
        case 'staff-login': {
            const method = node.authRef?.method;
            const entry = method && AUTH_METHOD_REGISTRY[method];
            if (!entry) return null;
            // Only render if this method is actually enabled for the branch
            // and currently the active tab — same two-condition gate the
            // legacy renderer applies (see Landing.jsx's registry-driven
            // switch), so a page can't surface a login method the branch
            // hasn't actually enabled.
            if (!portalData.loginMethods?.includes(method)) return null;
            if (authContext.userType !== entry.userType) return null;
            const Component = entry.component;
            return (
                <div className={loginClassFor(node, classFor(node, 'lp-login-form'))}>
                    <Component {...buildAuthProps(method, portalData, authContext, node)} />
                </div>
            );
        }
        case 'auth-method-selector':
            return <AuthMethodSelector portalData={portalData} authContext={authContext} className={classFor(node, 'lp-auth-method-selector')} />;
        case 'login-methods':
            return <LoginMethods node={node} portalData={portalData} authContext={authContext} className={loginClassFor(node, classFor(node, 'lp-login-methods'))} />;
        case 'login-submit':
            return (
                <div className={loginClassFor(node, 'lp-login-submit')}>
                    <TermsAndSubmit
                        authContext={authContext}
                        text={props.text}
                        agreeText={props.agreeText}
                        termsLinkText={props.termsLinkText}
                        buttonClassName={classFor(node, 'btn btn-primary btn-lg w-full mt-4')}
                    />
                </div>
            );
        case 'free-access-button': {
            // With back text it is a two-way switch, as the Oakwood page's link: on the
            // access code form it takes the guest back to the branch's other sign-in.
            const backTo = props.backText && authContext.userType === 'visitor'
                ? orderedMethods(portalData).map((key) => AUTH_METHOD_REGISTRY[key].userType).find((t) => t !== 'visitor')
                : null;
            return (
                <button
                    type="button"
                    className={classFor(node, 'btn btn-primary btn-lg w-full')}
                    onClick={() => authContext.setUserType(backTo || 'visitor')}
                >
                    {backTo ? props.backText : (props.text || 'Continue with Free Wi-Fi')}
                </button>
            );
        }
        case 'terms-conditions':
            return <div className={classFor(node, 'lp-terms-text')}>{portalData.termsAndConditions || props.text || 'Terms & Conditions apply.'}</div>;
        case 'privacy-policy':
            return <div className={classFor(node, 'lp-privacy-text')}>{props.text || 'See our Privacy Policy for details on how we handle your data.'}</div>;
        case 'hotel-info':
            return <div className={classFor(node, 'lp-hotel-info')}>{portalData.branchName || ''}</div>;
        case 'guest-welcome':
            return <div className={classFor(node, 'lp-guest-welcome')}>{portalData.welcomeMessage || 'Welcome!'}</div>;
        case 'qr-code':
            return <QRCodeSVG value={props.value || portalData.redirectURL || ''} size={props.size || 128} className={classFor(node)} />;
        case 'contact-info':
            return <div className={classFor(node, 'lp-contact-info')}>{props.text || ''}</div>;
        case 'scenic-portal':
            return <ScenicPortal node={node} portalData={portalData} authContext={authContext} className={classFor(node)} />;

        default:
            return null;
    }
}

/** The hotel name in place of a logo, for a company that has not uploaded one. */
function Wordmark({ node, portalData }) {
    const name = portalData.branchName || portalData.tenantName || 'Wi-Fi';
    return (
        <div className={classFor(node, 'lp-logo lp-wordmark')} role="img" aria-label={name}>
            {/* A plain faceted mark in the brand accent — drawn here, as icon fonts are
                loaded from a CDN a guest who is not signed in yet may not reach. */}
            <svg className="lp-wordmark-mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path className="lp-facet-side" d="M24 4 L30 24 L24 44 L18 24 Z" transform="rotate(-32 24 44)" />
                <path className="lp-facet-side lp-facet-far" d="M24 4 L30 24 L24 44 L18 24 Z" transform="rotate(32 24 44)" />
                <path className="lp-facet-front" d="M24 2 L31 24 L24 44 L17 24 Z" />
            </svg>
            <span className="lp-wordmark-name">{name}</span>
            {node.props?.tagline && <span className="lp-wordmark-tagline">{node.props.tagline}</span>}
        </div>
    );
}

function StatusRows({ rows }) {
    const shown = (rows || []).filter((row) => row && row.value);
    if (shown.length === 0) return null;
    return (
        <dl className="lp-status-rows">
            {shown.map((row) => (
                <div className="lp-status-row" key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                </div>
            ))}
        </dl>
    );
}

const CheckIcon = () => (
    <div className="lp-status-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
    </div>
);

/**
 * What the guest sees in the page's status box (or, on a page without one, in a card
 * of its own — Landing.jsx). `status` comes from Landing.jsx; the builder's live
 * preview passes sample ones:
 *   connected    — just signed in; the gateway takes them on in a few seconds, or at once with Continue
 *   online       — "My Wi-Fi": back on the portal while connected; the session, and Disconnect
 *   disconnected — after Disconnect: the session summary, and Connect again
 *   error        — the sign-in system did not answer
 */
export function StatusScreen({ status, portalData, onBack, look: lookProp }) {
    const look = useContext(PageLookContext) || lookProp || null;
    const [showDetails, setShowDetails] = useState(false);
    const hotel = portalData.organizationType === 'hotel';
    const className = ['lp-status', look ? `lp-look-${look}` : ''].filter(Boolean).join(' ');

    if (status.kind === 'online') {
        return (
            <div className={className} role="status">
                <CheckIcon />
                <h2 className="lp-status-title">You&apos;re connected</h2>
                <p className="lp-status-lead">
                    This device is online{portalData.branchName ? ` on ${portalData.branchName} Wi-Fi` : ''}.
                </p>
                <StatusRows rows={status.details} />
                <button type="button" className="lp-status-btn lp-status-btn-primary lp-status-btn-gap" onClick={status.onContinue}>
                    Continue browsing
                </button>
                <button type="button" className="lp-status-btn lp-status-btn-secondary" disabled={status.busy} onClick={status.onDisconnect}>
                    {status.busy ? 'Disconnecting…' : 'Disconnect'}
                </button>
            </div>
        );
    }

    if (status.kind === 'disconnected') {
        return (
            <div className={className} role="status">
                <h2 className="lp-status-title">You&apos;re disconnected</h2>
                <p className="lp-status-lead">
                    Your device is no longer using the {hotel ? 'hotel ' : ''}Wi-Fi. Here&apos;s a summary of the session.
                </p>
                <StatusRows rows={status.details} />
                <button type="button" className="lp-status-btn lp-status-btn-primary lp-status-btn-gap" onClick={status.onConnectAgain}>
                    Connect again
                </button>
            </div>
        );
    }

    if (status.kind === 'connected') {
        const details = (status.details || []).filter((row) => row && row.value);
        return (
            <div className={className} role="status">
                <CheckIcon />
                <h2 className="lp-status-title">You&apos;re connected</h2>
                <p className="lp-status-lead">{hotel ? 'Enjoy your stay. ' : ''}Your page will open in a few seconds.</p>
                <button type="button" className="lp-status-btn lp-status-btn-primary" onClick={status.onContinue}>Continue</button>
                {details.length > 0 && (
                    <button
                        type="button"
                        className="lp-status-btn lp-status-btn-secondary"
                        aria-expanded={showDetails}
                        onClick={() => setShowDetails((open) => !open)}
                    >
                        {showDetails ? 'Hide session details' : 'View session details'}
                    </button>
                )}
                {showDetails && <StatusRows rows={details} />}
                {/* Where "My Wi-Fi" is: the guest comes back here to see or end the connection. */}
                {status.manageHost && (
                    <p className="lp-status-note">To disconnect later, open <strong>{status.manageHost}</strong> on this device.</p>
                )}
            </div>
        );
    }

    return (
        <div className={className}>
            <h2 className="lp-status-title">Can&apos;t connect right now</h2>
            <div className="lp-status-alert" role="alert">
                {status.message || 'The sign-in system didn’t respond. Wait a moment and try again.'}
            </div>
            <p className="lp-status-lead">
                Go back and try again. If it keeps happening, {hotel ? 'reception' : 'a member of staff'} can check your access for you.
            </p>
            <button type="button" className="lp-status-btn lp-status-btn-primary" onClick={onBack}>Back to sign in</button>
        </div>
    );
}

/**
 * Presentational-only prop overlay per smart-component — the builder can
 * only ever set fields already whitelisted here, on top of the exact
 * functional props Landing.jsx already passes to these same components
 * today. There is no path from `node.props` into anything that reaches
 * the components' internal auth logic.
 */
// A sensible default accent for the two legacy components' cosmetic-only
// color props (OTPLoginFields' active-tab border/text,
// PMSLoginFields' ASR signup link) — sourced from THIS system's own design
// tokens (tokens.js), not the legacy Template.design.button.color concept,
// since a builder-made page has no such field. Purely cosmetic: every
// functional prop these components need is passed regardless. A node with a
// look gets none: its look's CSS colours those (the components set the accent
// inline, which no stylesheet could override).
const DEFAULT_ACCENT_COLOR = 'var(--violet-600)';

function buildAuthProps(method, portalData, ctx, node) {
    const props = node?.props || {};
    const accent = lookOf(node) ? undefined : DEFAULT_ACCENT_COLOR;
    const shared = {
        portalData,
        formData: ctx.formData,
        handleInputChange: ctx.handleInputChange,
    };
    switch (method) {
        case 'otp':
            return {
                ...shared,
                otpMethod: ctx.otpMethod,
                setOtpMethod: ctx.setOtpMethod,
                otpSent: ctx.otpSent,
                otpTimer: ctx.otpTimer,
                handleSendOTP: ctx.handleSendOTP,
                loading: ctx.loading,
                primaryColor: accent,
                btnStyle: {},
            };
        case 'pms':
            return {
                ...shared,
                odxLoginType: ctx.odxLoginType,
                setOdxLoginType: ctx.setOdxLoginType,
                showGenericPw: ctx.showGenericPw,
                setShowGenericPw: ctx.setShowGenericPw,
                primaryColor: accent,
                roomPlaceholder: props.roomPlaceholder || undefined,
                lastNameHint: props.lastNameHint || undefined,
            };
        case 'voucher':
        default:
            return shared;
    }
}

// Tab names a page can set (login-methods props); a name the branch set under
// Login Policy (portalData.methodLabels) still wins.
const TAB_LABEL_PROPS = {
    pms: 'pmsTabLabel', voucher: 'voucherTabLabel', otp: 'otpTabLabel', resident: 'residentTabLabel', staff: 'staffTabLabel',
};

// The branch's enabled methods the portal can render, in the order the branch set
// (Login Policy → method order) — the classic page's order too.
function orderedMethods(portalData) {
    const methods = (portalData.loginMethods || []).filter((m) => AUTH_METHOD_REGISTRY[m]);
    const order = portalData.methodOrder;
    if (!Array.isArray(order) || order.length === 0) return methods;
    return [...methods].sort((a, b) => {
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
}

function AuthMethodSelector({ portalData, authContext, className, labels }) {
    const methods = orderedMethods(portalData);
    if (methods.length === 0) return null;
    return (
        <div className={className}>
            {methods.map((key) => {
                const entry = AUTH_METHOD_REGISTRY[key];
                const icon = typeof entry.icon === 'function' ? entry.icon(portalData) : entry.icon;
                const label = typeof entry.label === 'function' ? entry.label(portalData) : entry.label;
                return (
                    <button
                        key={key}
                        type="button"
                        className={`lp-auth-tab ${authContext.userType === entry.userType ? 'active' : ''}`}
                        onClick={() => authContext.setUserType(entry.userType)}
                    >
                        <i className={`fas ${icon}`}></i> {portalData.methodLabels?.[key] || labels?.[TAB_LABEL_PROPS[key]] || label}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * The whole login box in one block — a tab for every method the branch has
 * enabled (and the portal can render), plus the form for the selected tab.
 * Same two rules the individual login components follow: a method the branch
 * hasn't enabled is never shown, and only the active tab's form renders.
 */
function LoginMethods({ node, portalData, authContext, className }) {
    const methods = orderedMethods(portalData);
    if (methods.length === 0) return null;
    const activeKey = methods.find((m) => AUTH_METHOD_REGISTRY[m].userType === authContext.userType);
    const ActiveForm = activeKey ? AUTH_METHOD_REGISTRY[activeKey].component : null;
    return (
        <div className={className}>
            {node.props?.showTabs !== 'no' && (
                <AuthMethodSelector portalData={portalData} authContext={authContext} className="lp-auth-method-selector" labels={node.props} />
            )}
            {/* Why the last attempt failed (wrong last name, unknown code, …) — stays
                until the guest edits a field, unlike the toast. */}
            {authContext.loginError && <div className="lp-login-error" role="alert">{authContext.loginError}</div>}
            {ActiveForm && <ActiveForm {...buildAuthProps(activeKey, portalData, authContext, node)} />}
        </div>
    );
}

// Only ever link to a web/site/mail/phone target — the backend already
// rejects javascript:/data: in props, this is the same rule enforced again
// at the point the href is actually rendered.
const SAFE_HREF = /^(https?:\/\/|\/|mailto:|tel:)/i;

function RichText({ text, href, className }) {
    const content = text || '';
    return (
        <p className={className}>
            {href && SAFE_HREF.test(href)
                ? <a href={href} target="_blank" rel="noopener noreferrer">{content}</a>
                : content}
        </p>
    );
}
